/**
 * Minimal ADF shape needed to read a prompt's text. Typed structurally rather than importing the
 * ADF schema so this module stays dependency-free.
 */
type AdfNode = {
	type?: string;
	text?: string;
	content?: AdfNode[];
};

/**
 * Nodes whose children are inline text runs. Every other node is a block, so its children each
 * start a new line — the prompt renderer splits on newlines to lay the prompt out, which would
 * otherwise run list items and paragraphs together.
 */
const INLINE_CONTAINERS = new Set(['paragraph', 'heading']);

/** Concatenated text of every text node, preserving the prompt's line structure. */
const adfText = (node: AdfNode): string => {
	if (node.type === 'hardBreak') {
		return '\n';
	}
	if (node.text !== undefined) {
		return node.text;
	}
	const parts = (node.content ?? []).map(adfText);
	return parts.join(INLINE_CONTAINERS.has(node.type ?? '') ? '' : '\n');
};

/**
 * The guard fields a PROMPT_BLOCKED stream message may carry. Both are absent on older backends.
 */
export type BlockedPromptFields = {
	redaction?: { redacted_prompt: string };
	human_message?: { content: string; content_mime_type: string };
};

/**
 * Text of a blocked prompt to render with scribbles over its `[REDACT]` / `[REDACTED]` tokens.
 *
 * Prefers the ADF `human_message` over `redaction.redacted_prompt`. The latter is markdown, and the
 * prompt bubble renders as plain text, so a redacted link arrives as `[[REDACT]](mailto:[REDACT])`
 * and would show its link syntax and two scribbles. The ADF carries the token as a single text
 * node, so reading its text yields one `[REDACT]` and no syntax.
 *
 * Returns undefined when the backend sends neither field, leaving the prompt unredacted.
 */
export const getBlockedPromptText = (streamMessage: BlockedPromptFields): string | undefined => {
	const humanMessage = streamMessage.human_message;
	if (humanMessage?.content_mime_type === 'text/adf') {
		try {
			const text = adfText(JSON.parse(humanMessage.content) as AdfNode).trim();
			if (text) {
				return text;
			}
		} catch {
			// Unparseable ADF — fall back to the markdown prompt rather than dropping the redaction.
		}
	}
	return streamMessage.redaction?.redacted_prompt;
};
